import React, { useState, useEffect } from 'react';
import useWindowWidth from '../hooks/useWindowWidth';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/layout/AppLayout';
import ExportDropdown from '../components/common/ExportDropdown';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const AGENT_COLORS = [
  { bg: '#EBF5FF', border: '#bfdbfe', text: '#1e40af', badge: '#0052CC' },
  { bg: '#f0fdf4', border: '#86efac', text: '#166534', badge: '#16a34a' },
  { bg: '#fef3c7', border: '#fde68a', text: '#92400e', badge: '#d97706' },
  { bg: '#fdf4ff', border: '#e9d5ff', text: '#6b21a8', badge: '#7c3aed' },
  { bg: '#fff1f2', border: '#fecdd3', text: '#9f1239', badge: '#e11d48' },
  { bg: '#f0fdfa', border: '#99f6e4', text: '#134e4a', badge: '#0d9488' },
];

const ERROR_LABELS = {
  // Layer 3 (rule-based) error types
  word_finding_difficulty: 'Word-Finding Difficulty',
  agrammatism: 'Agrammatism',
  perseveration: 'Perseveration',
  echolalia: 'Echolalia',
  low_lexical_continuity: 'Low Lexical Continuity',
  // Layer 2 (LLM-based) error types
  phonemic_paraphasia: 'Phonemic Paraphasia',
  semantic_paraphasia: 'Semantic Paraphasia',
  neologism: 'Neologism',
  jargon: 'Jargon',
  confabulation: 'Confabulation',
  circumlocution: 'Circumlocution',
  anomia: 'Anomia',
  tangentiality: 'Tangentiality',
};

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  sub: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
  backBtn: {
    backgroundColor: '#f3f4f6', color: '#374151', border: 'none',
    borderRadius: '8px', padding: '8px 16px', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer',
  },
  tabs: { display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '2px solid #e5e7eb', paddingBottom: '0' },
  tab: {
    padding: '10px 20px', fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', border: 'none', background: 'none',
    color: '#6b7280', borderBottom: '2px solid transparent',
    marginBottom: '-2px', fontFamily: 'inherit',
  },
  tabActive: { color: '#0052CC', borderBottom: '2px solid #0052CC' },
  layout: { display: 'grid', gridTemplateColumns: '1fr 320px', gap: '24px', alignItems: 'start' },
  card: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '13px', fontWeight: '700', color: '#0052CC',
    textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px',
    paddingBottom: '10px', borderBottom: '1px solid #e5e7eb',
  },
  turn: { marginBottom: '20px' },
  turnHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' },
  agentBadge: { fontSize: '11px', fontWeight: '700', padding: '2px 10px', borderRadius: '20px', color: '#fff' },
  turnNumber: { fontSize: '11px', color: '#9ca3af' },
  turnTimestamp: { fontSize: '11px', color: '#9ca3af', marginLeft: 'auto' },
  turnContent: { fontSize: '14px', lineHeight: '1.6', borderRadius: '8px', padding: '12px 16px', borderLeft: '3px solid' },
  errorChip: {
    display: 'inline-block', fontSize: '10px', fontWeight: '700',
    padding: '1px 6px', borderRadius: '10px', marginRight: '4px', marginTop: '4px',
    backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5',
  },
  sideCard: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '20px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    marginBottom: '16px',
  },
  overallScore: {
    textAlign: 'center', padding: '20px',
    backgroundColor: '#f9fafb', borderRadius: '8px',
    border: '1px solid #e5e7eb', marginBottom: '16px',
  },
  overallScoreValue: { fontSize: '52px', fontWeight: '800' },
  overallScoreLabel: { fontSize: '12px', color: '#6b7280', marginTop: '4px' },
  scoreRow: { marginBottom: '14px' },
  scoreHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' },
  scoreLabel: { fontSize: '13px', fontWeight: '600', color: '#374151' },
  scoreValue: { fontSize: '13px', fontWeight: '700' },
  scoreBar: { width: '100%', height: '5px', backgroundColor: '#e5e7eb', borderRadius: '3px', marginBottom: '4px', overflow: 'hidden' },
  scoreBarFill: { height: '100%', borderRadius: '3px', transition: 'width 0.5s ease' },
  scoreRationale: { fontSize: '11px', color: '#6b7280', lineHeight: '1.5' },
  metricGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' },
  metricCard: { backgroundColor: '#f9fafb', borderRadius: '8px', padding: '12px', border: '1px solid #e5e7eb', textAlign: 'center' },
  metricValue: { fontSize: '20px', fontWeight: '800', color: '#0052CC' },
  metricLabel: { fontSize: '10px', color: '#6b7280', marginTop: '2px' },
  agentSection: { marginBottom: '16px', padding: '14px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' },
  agentSectionTitle: { fontSize: '13px', fontWeight: '700', color: '#111827', marginBottom: '10px' },
  agentMetricRow: { display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280', marginBottom: '6px' },
  agentMetricVal: { fontWeight: '700', color: '#374151' },
  severityBar: { width: '100%', height: '6px', backgroundColor: '#e5e7eb', borderRadius: '3px', overflow: 'hidden', marginTop: '8px' },
  errorRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f3f4f6', fontSize: '13px' },
  errorLabel: { color: '#374151', fontWeight: '500' },
  errorCount: { fontWeight: '700', color: '#dc2626' },
  summaryText: { fontSize: '13px', color: '#374151', lineHeight: '1.7', backgroundColor: '#f9fafb', borderRadius: '8px', padding: '12px', marginBottom: '16px' },
  strengthsWeaknesses: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' },
  swCard: { borderRadius: '8px', padding: '12px' },
  swTitle: { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
  swItem: { fontSize: '12px', color: '#374151', marginBottom: '4px', lineHeight: '1.5' },
  exportBtn: { width: '100%', padding: '10px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginBottom: '8px', textAlign: 'left' },
  exportBtnJson: { backgroundColor: '#EBF5FF', color: '#0052CC' },
  exportBtnCsv: { backgroundColor: '#f0fdf4', color: '#16a34a' },
  exportBtnTxt: { backgroundColor: '#f9fafb', color: '#374151' },
  runAgainBtn: { width: '100%', padding: '10px', backgroundColor: '#0052CC', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', marginTop: '8px' },
  infoRow: { fontSize: '13px', color: '#6b7280', marginBottom: '8px' },
  infoVal: { fontWeight: '600', color: '#374151' },
  statusBadge: { display: 'inline-block', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', marginBottom: '12px' },
  loading: { textAlign: 'center', padding: '64px', color: '#6b7280' },
  error: { backgroundColor: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', marginBottom: '16px' },
  evalPending: { textAlign: 'center', padding: '24px', color: '#9ca3af', fontSize: '13px', fontStyle: 'italic' },
};

const STATUS_COLORS = {
  completed: { backgroundColor: '#dcfce7', color: '#16a34a' },
  stopped: { backgroundColor: '#fef3c7', color: '#d97706' },
  failed: { backgroundColor: '#fef2f2', color: '#dc2626' },
};

function getScoreColor(score) {
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#0052CC';
  if (score >= 40) return '#d97706';
  return '#dc2626';
}

function getSeverityColor(index) {
  if (index < 0.1) return '#16a34a';
  if (index < 0.3) return '#d97706';
  return '#dc2626';
}

export default function Results() {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('transcript');
  const [evaluationLoading, setEvaluationLoading] = useState(false);
  const [simulation, setSimulation] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const width = useWindowWidth();
  const isMobile = width < 768;

  useEffect(() => { fetchData(); }, [id]);

  async function fetchData() {
    try {
      setLoading(true);
      const [simRes, transcriptRes] = await Promise.all([
        fetch(`${API}/api/simulations/${id}`),
        fetch(`${API}/api/simulations/${id}/transcript`),
      ]);
      const simData = await simRes.json();
      const transcriptData = await transcriptRes.json();
      setSimulation(simData);
      setTranscript(Array.isArray(transcriptData) ? transcriptData : []);
      setLoading(false); // show page immediately — transcript doesn't need evaluation

      // Fetch evaluation separately without blocking the page
      fetchEvaluation();
    } catch {
      setError('Failed to load results.');
      setLoading(false);
    }
  }

  async function fetchEvaluation() {
    setEvaluationLoading(true);
    try {
      for (let attempt = 0; attempt < 8; attempt++) {
        const evalRes = await fetch(`${API}/api/evaluations/${id}`);
        if (evalRes.ok) {
          const evalData = await evalRes.json();
          setEvaluation(evalData);
          break;
        }
        await new Promise(r => setTimeout(r, 2500));
      }
    } catch { }
    setEvaluationLoading(false);
  }

  // Build agent color map
  const agentColorMap = {};
  let colorIdx = 0;
  transcript.forEach(turn => {
    if (!agentColorMap[turn.agent_id]) {
      agentColorMap[turn.agent_id] = AGENT_COLORS[colorIdx % AGENT_COLORS.length];
      colorIdx++;
    }
  });

  // Build turn error map from evaluation
  const turnErrorMap = {};
  if (evaluation?.turn_level) {
    evaluation.turn_level.forEach(t => {
      if (t.detected_errors?.length > 0) {
        turnErrorMap[t.turn_number] = t.detected_errors;
      }
    });
  }

  function downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }

  function exportJSON() {
    downloadFile(JSON.stringify({ simulation, transcript, evaluation }, null, 2),
      `v2vlab_results_${id}.json`, 'application/json');
  }

  function exportCSV() {
    const rows = [
      ['turn_number', 'agent_name', 'agent_role', 'content', 'perceived_content', 'errors', 'timestamp'],
      ...transcript.map(t => {
        const errors = (turnErrorMap[t.turn_number] || []).map(e => e.type).join(';');
        return [
          t.turn_number, t.agent_name, t.agent_role || '',
          `"${(t.content || '').replace(/"/g, '""')}"`,
          `"${(t.perceived_content || '').replace(/"/g, '""')}"`,
          errors, t.timestamp,
        ];
      }),
    ];
    downloadFile(rows.map(r => r.join(',')).join('\n'), `v2vlab_transcript_${id}.csv`, 'text/csv');
  }

  function exportTXT() {
    const lines = [
      'V2VLab Simulation Results',
      `Scenario: ${simulation?.scenario_title}`,
      `Date: ${new Date(simulation?.created_at).toLocaleString()}`,
      `Status: ${simulation?.status}`,
      `Total Turns: ${transcript.length}`,
      '', '─'.repeat(60), 'TRANSCRIPT', '─'.repeat(60), '',
      ...transcript.map(t =>
        `[Turn ${t.turn_number}] ${t.agent_name}${t.agent_role ? ` (${t.agent_role})` : ''}:\n${t.content}\n`
      ),
    ];
    if (evaluation) {
      lines.push('', '─'.repeat(60), 'EVALUATION', '─'.repeat(60), '');
      lines.push(`Overall Score: ${evaluation.overall_score}/100`);
      lines.push(`Summary: ${evaluation.summary}`);
    }
    downloadFile(lines.join('\n'), `v2vlab_results_${id}.txt`, 'text/plain');
  }

  const totalWords = transcript.reduce((sum, t) => sum + (t.content?.split(' ').length || 0), 0);
  const avgWordsPerTurn = transcript.length > 0 ? Math.round(totalWords / transcript.length) : 0;

  if (loading) return <AppLayout><div style={styles.loading}>Loading results...</div></AppLayout>;
  if (error) return <AppLayout><div style={styles.error}>{error}</div></AppLayout>;
  if (!simulation) return <AppLayout><div style={styles.loading}>No simulation found.</div></AppLayout>;

  const TABS = [
    { id: 'transcript', label: 'Transcript' },
    { id: 'evaluation', label: 'Evaluation' },
    { id: 'computational', label: 'Computational Metrics' },
    { id: 'disorders', label: 'Disorder Analysis' },
  ];

  return (
    <AppLayout>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>{simulation.scenario_title}</div>
          <div style={styles.sub}>{transcript.length} turns · {totalWords} words · {new Date(simulation.created_at).toLocaleDateString()}</div>
        </div>
        <button style={styles.backBtn} onClick={() => navigate('/scenarios')}>← Scenarios</button>
      </div>

      {/* Tabs */}
      <div style={{ ...styles.tabs, overflowX: isMobile ? 'auto' : 'visible', flexWrap: isMobile ? 'nowrap' : 'wrap' }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            style={{ ...styles.tab, ...(activeTab === tab.id ? styles.tabActive : {}) }}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ ...styles.metricGrid, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
        {/* Main content */}
        <div>
          {/* TRANSCRIPT TAB */}
          {activeTab === 'transcript' && (
            <div style={styles.card}>
              <div style={styles.sectionTitle}>Full Transcript</div>
              {transcript.map(turn => {
                const colorScheme = agentColorMap[turn.agent_id] || AGENT_COLORS[0];
                const errors = turnErrorMap[turn.turn_number] || [];
                return (
                  <div key={turn.id || turn.turn_number} style={styles.turn}>
                    <div style={styles.turnHeader}>
                      <div style={{ ...styles.agentBadge, backgroundColor: colorScheme.badge }}>
                        {turn.agent_name}{turn.agent_role ? ` · ${turn.agent_role}` : ''}
                      </div>
                      <div style={styles.turnNumber}>Turn {turn.turn_number}</div>
                      <div style={styles.turnTimestamp}>{new Date(turn.timestamp).toLocaleTimeString()}</div>
                    </div>
                    <div style={{ ...styles.turnContent, backgroundColor: colorScheme.bg, borderLeftColor: colorScheme.badge, color: colorScheme.text }}>
                      {turn.content}
                    </div>
                    {errors.length > 0 && (
                      <div style={{ marginTop: '6px' }}>
                        {[...new Set(errors.map(e => e.type))].map(type => (
                          <span key={type} style={styles.errorChip}>
                            {ERROR_LABELS[type] || type}
                          </span>
                        ))}
                      </div>
                    )}
                    {turn.perturbation_applied && (
                      <div style={{ display: 'flex', gap: '6px', marginTop: '6px', alignItems: 'center' }}>
                        <span style={{
                          fontSize: '10px', fontWeight: '700', padding: '2px 7px',
                          borderRadius: '10px',
                          backgroundColor: turn.perturbation_mode === 'parametric' ? '#dbeafe' : '#f3f4f6',
                          color: turn.perturbation_mode === 'parametric' ? '#0052CC' : '#6b7280',
                        }}>
                          {turn.perturbation_mode === 'parametric' ? '✦ Parametric' : 'Categorical'}
                        </span>
                      </div>
                    )}
                    {turn.perceived_content && turn.perturbation_applied && (
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px', padding: '6px 10px', backgroundColor: '#fef3c7', borderRadius: '6px', borderLeft: '3px solid #d97706' }}>
                        🎙 Perceived: "{turn.perceived_content}"
                      </div>
                    )}
                    {turn.audio_url && (
                      <audio controls src={turn.audio_url} style={{ width: '100%', marginTop: '8px', height: '32px' }} />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* EVALUATION TAB */}
          {activeTab === 'evaluation' && evaluationLoading && !evaluation && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af', fontSize: '14px' }}>
              ⏳ Evaluation in progress — this may take up to 20 seconds...
            </div>
          )}
          {activeTab === 'evaluation' && (
            <div>
              {!evaluation ? (
                <div style={styles.card}><div style={styles.evalPending}>Evaluation not available for this simulation.</div></div>
              ) : (
                <>
                  <div style={styles.card}>
                    <div style={styles.sectionTitle}>Overall Score</div>
                    <div style={styles.overallScore}>
                      <div style={{ ...styles.overallScoreValue, color: evaluation.overall_score != null ? getScoreColor(evaluation.overall_score) : '#9ca3af' }}>
                        {evaluation.overall_score ?? '—'}
                      </div>
                      <div style={styles.overallScoreLabel}>/ 100</div>
                    </div>
                    {evaluation.overall_score_basis === 'unweighted_mean_fallback' && (
                      <div style={{ fontSize: '11px', color: '#d97706', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '8px 10px', marginTop: '8px', marginBottom: '8px' }}>
                        ⚠️ Unweighted mean of scorable metrics — not a validated composite. Define scenario metric weights for a scenario-specific score.
                      </div>
                    )}
                    {evaluation.summary && <div style={styles.summaryText}>{evaluation.summary}</div>}
                    {(evaluation.strengths?.length > 0 || evaluation.limitations?.length > 0) && (
                      <div style={{ ...styles.strengthsWeaknesses, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
                        <div style={{ ...styles.swCard, backgroundColor: '#f0fdf4', border: '1px solid #86efac' }}>
                          <div style={{ ...styles.swTitle, color: '#16a34a' }}>✓ Strengths</div>
                          {(evaluation.strengths || []).map((s, i) => (
                            <div key={i} style={styles.swItem}>• {s}</div>
                          ))}
                        </div>
                        <div style={{ ...styles.swCard, backgroundColor: '#fef2f2', border: '1px solid #fca5a5' }}>
                          <div style={{ ...styles.swTitle, color: '#dc2626' }}>✗ Limitations</div>
                          {(evaluation.limitations || []).map((w, i) => (
                            <div key={i} style={styles.swItem}>• {w}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={styles.card}>
                    <div style={styles.sectionTitle}>LLM-Scored Metrics {evaluation.judge?.runs > 1 ? `(${evaluation.judge.runs}-run median)` : ''}</div>
                    {Object.entries(evaluation.llm_metrics || {}).map(([key, data]) => {
                      if (!data) return null;
                      const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                      if (!data.scorable) {
                        return (
                          <div key={key} style={styles.scoreRow}>
                            <div style={styles.scoreHeader}>
                              <div style={styles.scoreLabel}>{label}</div>
                              <div style={{ ...styles.scoreValue, color: '#9ca3af' }}>Not scorable</div>
                            </div>
                            <div style={{ ...styles.scoreRationale, color: '#9ca3af' }}>
                              {data.not_scorable_reason || 'Insufficient evidence in transcript.'}
                            </div>
                          </div>
                        );
                      }
                      const score = data.score_0_100;
                      const agreement = data.judge_agreement;
                      return (
                        <div key={key} style={styles.scoreRow}>
                          <div style={styles.scoreHeader}>
                            <div style={styles.scoreLabel}>{label}</div>
                            <div style={{ ...styles.scoreValue, color: getScoreColor(score) }}>{score}/100</div>
                          </div>
                          <div style={styles.scoreBar}>
                            <div style={{ ...styles.scoreBarFill, width: `${score}%`, backgroundColor: getScoreColor(score) }} />
                          </div>
                          {data.rationale && <div style={styles.scoreRationale}>{data.rationale}</div>}
                          {agreement && agreement.n_scorable_runs > 1 && (
                            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                              Judge agreement: <span style={{
                                fontWeight: '700',
                                color: agreement.agreement_level === 'high' ? '#16a34a' : agreement.agreement_level === 'moderate' ? '#d97706' : '#dc2626',
                              }}>{agreement.agreement_level}</span> (scores: {agreement.individual_scores_0_4?.join(', ')} on 0-4 scale, range {agreement.score_range_0_4})
                            </div>
                          )}
                          {data.evidence && data.evidence.length > 0 && (
                            <div style={{ marginTop: '6px' }}>
                              {data.evidence.slice(0, 2).map((ev, i) => (
                                <div key={i} style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic', marginTop: '2px' }}>
                                  Turn {ev.turn_number}: "{ev.excerpt}"
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {evaluation.participation && (
                    <div style={styles.card}>
                      <div style={styles.sectionTitle}>Participation (Descriptive)</div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '12px' }}>
                        {evaluation.participation.interpretation_note}
                      </div>
                      <div style={styles.metricGrid}>
                        <div style={styles.metricCard}>
                          <div style={styles.metricValue}>{evaluation.participation.participation_balance_0_100}</div>
                          <div style={styles.metricLabel}>Balance (0-100, entropy-based)</div>
                        </div>
                        <div style={styles.metricCard}>
                          <div style={styles.metricValue}>{evaluation.participation.basis === 'speaking_time' ? 'Speaking Time' : 'Word Count'}</div>
                          <div style={styles.metricLabel}>Measurement Basis</div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* COMPUTATIONAL METRICS TAB */}
          {activeTab === 'computational' && (
            <div>
              {!evaluation?.computational_metrics ? (
                <div style={styles.card}><div style={styles.evalPending}>Computational metrics not available.</div></div>
              ) : (
                <>
                  {/* Global metrics */}
                  <div style={styles.card}>
                    <div style={styles.sectionTitle}>Global Metrics</div>
                    <div style={styles.metricGrid}>
                      {[
                        { label: 'Total Turns', value: evaluation.computational_metrics.global?.total_turns ?? '—' },
                        { label: 'Total Words', value: evaluation.computational_metrics.global?.total_words ?? '—' },
                        { label: 'Turn Balance', value: evaluation.computational_metrics.global?.turn_balance_score != null ? `${evaluation.computational_metrics.global.turn_balance_score}%` : '—' },
                        { label: 'Topic Coherence (TF-IDF)', value: evaluation.computational_metrics.global?.mean_topic_coherence != null ? `${(evaluation.computational_metrics.global.mean_topic_coherence * 100).toFixed(1)}%` : '—' },
                        { label: 'Lexical Entrainment', value: evaluation.computational_metrics.global?.lexical_entrainment != null ? `${(evaluation.computational_metrics.global.lexical_entrainment * 100).toFixed(1)}%` : '—' },
                      ].map(m => (
                        <div key={m.label} style={styles.metricCard}>
                          <div style={styles.metricValue}>{m.value}</div>
                          <div style={styles.metricLabel}>{m.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* STT metrics */}
                  {evaluation.computational_metrics.stt_metrics && (
                    <div style={styles.card}>
                      <div style={styles.sectionTitle}>STT Accuracy Metrics</div>
                      <div style={styles.metricGrid}>
                        <div style={styles.metricCard}>
                          <div style={{ ...styles.metricValue, color: evaluation.computational_metrics.stt_metrics.mean_wer > 0.3 ? '#dc2626' : '#16a34a' }}>
                            {evaluation.computational_metrics.stt_metrics.mean_wer != null ? `${(evaluation.computational_metrics.stt_metrics.mean_wer * 100).toFixed(1)}%` : '—'}
                          </div>
                          <div style={styles.metricLabel}>Mean WER</div>
                        </div>
                        <div style={styles.metricCard}>
                          <div style={{ ...styles.metricValue, color: '#0052CC' }}>
                            {evaluation.computational_metrics.stt_metrics.mean_cer != null ? `${(evaluation.computational_metrics.stt_metrics.mean_cer * 100).toFixed(1)}%` : '—'}
                          </div>
                          <div style={styles.metricLabel}>Mean CER</div>
                        </div>
                        <div style={styles.metricCard}>
                          <div style={styles.metricValue}>{evaluation.computational_metrics.stt_metrics.perturbed_turn_count ?? '—'}</div>
                          <div style={styles.metricLabel}>Perturbed Turns</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Per-agent computational */}
                  {Object.entries(evaluation.computational_metrics.per_agent || {}).map(([agentId, data]) => {
                    const color = agentColorMap[agentId] || AGENT_COLORS[0];
                    return (
                      <div key={agentId} style={{ ...styles.card, borderLeft: `4px solid ${color.badge}` }}>
                        <div style={styles.sectionTitle}>{data.agent_name}</div>
                        <div style={styles.metricGrid}>
                          {[
                            { label: 'Turns', value: data.turn_count },
                            { label: 'Total Words', value: data.total_words },
                            { label: 'MLU (words/utterance)', value: data.mlu_w },
                            { label: 'MATTR (lexical diversity)', value: data.mattr },
                            { label: 'Content Word Ratio', value: data.content_word_ratio },
                            { label: 'Filled Pauses', value: `${data.filled_pause_count} (${data.filled_pause_rate}/100w)` },
                            { label: 'Hedge Words', value: `${data.hedge_word_count} (${data.hedge_word_rate}/100w)` },
                            { label: 'Revisions', value: `${data.revision_count} (${data.revision_rate}/100w)` },
                            { label: 'Abandoned Utterances', value: `${data.abandoned_utterance_count} (${data.abandoned_utterance_rate}/100w)` },
                          ].map(m => (
                            <div key={m.label} style={styles.metricCard}>
                              <div style={styles.metricValue}>{m.value ?? '—'}</div>
                              <div style={styles.metricLabel}>{m.label}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {/* DISORDER ANALYSIS TAB */}
          {activeTab === 'disorders' && (
            <div>
              {!evaluation?.disorder_metrics ? (
                <div style={styles.card}><div style={styles.evalPending}>Disorder analysis not available.</div></div>
              ) : (
                Object.entries(evaluation.disorder_metrics.per_agent || {}).map(([agentId, entry]) => {
                  const color = agentColorMap[agentId] || AGENT_COLORS[0];
                  const data = entry.rule_based || {};
                  const llmData = entry.llm_based || {};
                  const severityPct = Math.round((data.severity_index || 0) * 100);
                  const severityColor = getSeverityColor(data.severity_index || 0);
                  return (
                    <div key={agentId} style={{ ...styles.card, borderLeft: `4px solid ${color.badge}` }}>
                      <div style={styles.sectionTitle}>{entry.agent_name}</div>

                      {/* Severity Index */}
                      <div style={{ marginBottom: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Disorder Severity Index</span>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: severityColor }}>{data.severity_index?.toFixed(3) ?? '0.000'}</span>
                        </div>
                        <div style={styles.severityBar}>
                          <div style={{ height: '100%', width: `${Math.min(severityPct * 3, 100)}%`, backgroundColor: severityColor, borderRadius: '3px', transition: 'width 0.5s' }} />
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                          Scale: 0 (no errors) → 1 (severe) · Total errors detected: {data.total_errors}
                        </div>
                        {data.severity_note && (
                          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px', fontStyle: 'italic' }}>
                            {data.severity_note}
                          </div>
                        )}
                      </div>

                      {/* Error counts */}
                      <div>
                        {Object.entries(data.error_counts || {}).map(([type, count]) => {
                          if (count === 0) return null;
                          return (
                            <div key={type} style={styles.errorRow}>
                              <span style={styles.errorLabel}>{ERROR_LABELS[type] || type}</span>
                              <span style={styles.errorCount}>{count}</span>
                            </div>
                          );
                        })}
                        {Object.values(data.error_counts || {}).every(c => c === 0) && (
                          <div style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>No errors detected for this agent.</div>
                        )}
                      </div>

                      {/* Error examples */}
                      {Object.entries(data.error_examples || {}).map(([type, examples]) => (
                        examples.length > 0 && (
                          <div key={type} style={{ marginTop: '16px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              {ERROR_LABELS[type] || type} Examples
                            </div>
                            {examples.map((ex, i) => (
                              <div key={i} style={{ fontSize: '12px', color: '#374151', backgroundColor: '#fef2f2', borderRadius: '6px', padding: '8px 10px', marginBottom: '6px', borderLeft: '3px solid #fca5a5' }}>
                                <div style={{ fontWeight: '600', marginBottom: '2px' }}>Turn {ex.turn_number}</div>
                                <div style={{ color: '#6b7280' }}>{ex.text?.substring(0, 120)}{ex.text?.length > 120 ? '...' : ''}</div>
                                {ex.detail && <div style={{ color: '#9ca3af', marginTop: '2px', fontStyle: 'italic' }}>{ex.detail}</div>}
                              </div>
                            ))}
                          </div>
                        )
                      ))}

                      {/* LLM-detected features (source text) */}
                      {Object.values(llmData.feature_counts || {}).some(v => v > 0) && (
                        <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#0052CC', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            LLM-Detected Features (Source Text)
                          </div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '10px' }}>
                            Descriptive clinical-linguistic annotation, not diagnosis. See per-turn evidence for detail.
                          </div>
                          {Object.entries(llmData.feature_counts || {}).map(([type, counts]) => {
                            const total = (counts?.observed || 0) + (counts?.possible || 0);
                            if (total === 0) return null;
                            return (
                              <div key={type} style={styles.errorRow}>
                                <span style={styles.errorLabel}>{ERROR_LABELS[type] || type}</span>
                                <span style={styles.errorCount}>
                                  {counts.observed > 0 && `${counts.observed} observed`}
                                  {counts.observed > 0 && counts.possible > 0 && ', '}
                                  {counts.possible > 0 && `${counts.possible} possible`}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
        {/* Sidebar */}
        <div>
          {/* Simulation Info */}
          <div style={styles.sideCard}>
            <div style={styles.sectionTitle}>Simulation Info</div>
            <div style={{ ...styles.statusBadge, ...(STATUS_COLORS[simulation.status] || STATUS_COLORS.completed) }}>
              {simulation.status.toUpperCase()}
            </div>
            <div style={styles.infoRow}>Scenario: <span style={styles.infoVal}>{simulation.scenario_title}</span></div>
            <div style={styles.infoRow}>Turns: <span style={styles.infoVal}>{transcript.length}</span></div>
            <div style={styles.infoRow}>Words: <span style={styles.infoVal}>{totalWords}</span></div>
            <div style={styles.infoRow}>Avg/Turn: <span style={styles.infoVal}>{avgWordsPerTurn} words</span></div>
            <div style={styles.infoRow}>Date: <span style={styles.infoVal}>{new Date(simulation.created_at).toLocaleDateString()}</span></div>
          </div>

          {/* Quick eval summary */}
          {evaluation && (
            <div style={styles.sideCard}>
              <div style={styles.sectionTitle}>Evaluation Summary</div>
              <div style={styles.overallScore}>
                <div style={{ ...styles.overallScoreValue, color: getScoreColor(evaluation.overall_score) }}>
                  {evaluation.overall_score}
                </div>
                <div style={styles.overallScoreLabel}>Overall Score / 100</div>
              </div>
              {evaluation.summary && (
                <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.6' }}>{evaluation.summary}</div>
              )}
            </div>
          )}

          {/* Export */}
          <div style={styles.sideCard}>
            <ExportDropdown
              fullWidth
              align="left"
              options={[
                { label: '⬇ PDF Report', sublabel: 'Formatted provenance document', onClick: () => window.open(
                  `${API}/api/reports/simulation/${id}?owner_id=${currentUser.uid}`,
                  '_blank'
                ) },
                { label: '⬇ JSON', sublabel: 'Full structured data', onClick: exportJSON },
                { label: '⬇ CSV', sublabel: 'Transcript as spreadsheet', onClick: exportCSV },
                { label: '⬇ TXT', sublabel: 'Plain text transcript', onClick: exportTXT },
              ]}
            />
            
            <button style={styles.runAgainBtn} onClick={() => navigate(`/simulations/new?scenario=${simulation.scenario_id}`)}>
              ↺ Run Again
            </button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
